import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Filter, Search, MoveHorizontal, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { all: "All", win: "Wins", loss: "Losses", draw: "Draws" };

export default function Predictions() {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [pred, st] = await Promise.all([
          axios.get(`${API}/predictions`),
          axios.get(`${API}/stats`),
        ]);
        setData(pred.data);
        setStats(st.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return data.filter((p) => {
      const statusOk = filter === "all" || p.status === filter;
      const q = search.trim().toLowerCase();
      const searchOk =
        !q ||
        p.match.toLowerCase().includes(q) ||
        p.competition.toLowerCase().includes(q) ||
        p.pick.toLowerCase().includes(q);
      return statusOk && searchOk;
    });
  }, [data, filter, search]);

  return (
    <div data-testid="predictions-page">
      <SEO
        title="Previous Subscription Tips — Asian Handicap Archive"
        description="Public archive of every Asian Handicap tip we've published. 1,114+ lifetime tips at 81.1% win rate. Filter, search, verify — the record is the product."
        path="/predictions"
        keywords="Asian Handicap archive, AH tips history, football predictions results, verified AH tipster, football betting archive"
      />
      {/* Page header */}
      <section className="bg-ink-950 text-white py-12 sm:py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-[10px] sm:text-xs mb-2 sm:mb-3">
            Previous Subscription Tips
          </div>
          <h1
            data-testid="predictions-title"
            className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl"
          >
            Every Asian Handicap tip we've published — <span className="gold-text">receipt-style</span>.
          </h1>
          <p className="mt-4 sm:mt-5 max-w-2xl text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed">
            Filter by result, search by team or league. Nothing here is hidden,
            edited or removed — the record is the product.
          </p>

          {stats && (
            <div
              data-testid="archive-stats"
              className="mt-8 sm:mt-10 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4 max-w-3xl"
            >
              {[
                { label: "Total Tips", value: stats.total_tips, testid: "arch-total" },
                { label: "Wins", value: stats.wins, testid: "arch-wins" },
                { label: "Losses", value: stats.losses, testid: "arch-losses" },
                { label: "Win Rate", value: `${stats.win_rate}%`, testid: "arch-rate" },
              ].map((s) => (
                <div
                  key={s.label}
                  data-testid={s.testid}
                  className="p-3 sm:p-5 rounded-xl border border-white/10 bg-white/[0.03]"
                >
                  <div className="font-display font-bold text-xl sm:text-2xl lg:text-3xl text-white">
                    {s.value}
                  </div>
                  <div className="text-[10px] sm:text-xs lg:text-sm text-white/50 uppercase tracking-wider mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Filters + table */}
      <section className="py-10 sm:py-14 lg:py-20 bg-ink-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:justify-between mb-5 sm:mb-6">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <Filter className="w-4 h-4 text-ink-500 hidden xs:inline" />
              {Object.entries(STATUS_LABELS).map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`filter-${k}`}
                  onClick={() => setFilter(k)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transition-all ${
                    filter === k
                      ? "bg-emerald-700 border-emerald-700 text-white"
                      : "bg-white border-ink-200 text-ink-700 hover:border-emerald-700/50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                data-testid="archive-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search team, league, pick…"
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-ink-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent"
              />
            </div>
          </div>

          {/* Mobile-only scroll hint */}
          <div className="flex sm:hidden items-center justify-center gap-1.5 mb-2 text-[11px] text-ink-500 font-mono uppercase tracking-wider">
            <MoveHorizontal className="w-3.5 h-3.5" />
            Swipe to see all columns
          </div>

          {/* Table */}
          <div
            data-testid="archive-table-wrapper"
            className="table-scroll overflow-x-auto bg-white rounded-xl sm:rounded-2xl border border-ink-200 shadow-sm -mx-1 sm:mx-0"
          >
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left font-mono uppercase tracking-wider text-[10px] sm:text-[11px] text-ink-500">
                  <th className="sticky left-0 bg-ink-50 z-10 px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] sm:shadow-none">Date</th>
                  <th className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap">Match</th>
                  <th className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap">Competition</th>
                  <th className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap">AH Pick</th>
                  <th className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-right">Odds</th>
                  <th className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-center">Status</th>
                </tr>
              </thead>
              <tbody data-testid="archive-tbody">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-ink-500">
                      Loading archive…
                    </td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-ink-500">
                      No tips match your filters.
                    </td>
                  </tr>
                )}
                {filtered.map((p, i) => (
                  <tr
                    key={`${p.date}-${p.match}-${i}`}
                    data-testid={`archive-row-${i}`}
                    className="group border-b border-ink-100 hover:bg-emerald-700/[0.03] transition-colors"
                  >
                    <td className="sticky left-0 bg-white group-hover:bg-[#f3fbf9] z-10 px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap font-mono text-[11px] sm:text-xs text-ink-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] sm:shadow-none">
                      {p.date}
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap font-semibold text-ink-900 text-xs sm:text-sm">
                      {p.match}
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-ink-500 text-xs sm:text-sm">
                      {p.competition}
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md bg-emerald-700/10 text-emerald-800 font-mono font-semibold text-[11px] sm:text-xs">
                        {p.pick}
                      </span>
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-right font-mono font-semibold text-ink-900 text-xs sm:text-sm">
                      {p.odds.toFixed(2)}
                    </td>
                    <td className="px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-center">
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 sm:mt-6 px-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[11px] sm:text-xs text-ink-500 font-mono leading-relaxed">
              Showing the last {filtered.length} settled tips · {stats ? stats.total_tips.toLocaleString("en-US") : "…"} total lifetime tips
            </p>
            <Link
              to="/contact"
              data-testid="archive-subscribe-cta"
              className="inline-flex items-center gap-2 self-start sm:self-auto px-5 py-2.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm transition-all shadow-lg shadow-emerald-700/20 hover:shadow-emerald-700/40"
            >
              Subscribe for Full Archives
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
